/* eslint-disable react/jsx-no-bind */
// @flow
import React, { useState, type Node } from 'react';
import { rankWith, uiTypeIs } from '@jsonforms/core';

import {
    InlineField,
    Flex,
    InlineFieldAction,
    InlineFieldActions,
    type InlineFieldProps
} from '@locus-taxy/neo-ui';
import MultiChipInput from './MultiChipInput';
import MaraOnlyTag from '../../components/MaraOnlyTag';

import theme from './MultiChipTextField.scss';

type Props = {
    ...InlineFieldProps,
    value: Array<string> | string,
    onChange: (value: Array<string>) => void,
    info?: string,
    min?: number | string,
    max?: number | string,
    isMaraOnlyField?: boolean,
    label: string
};

const MultiChipTextField = ({
    label,
    value,
    isMaraOnlyField,
    onChange
}: Props): Node => {
    const [isInputFocused, setIsFocused] = useState(false);

    const handleFocus = (event: SyntheticInputEvent<HTMLInputElement>) => {
        event.target.setAttribute('autocomplete', 'off');
        setIsFocused(true);
    };

    const handleBlur = () => setIsFocused(false); // This is to keep the existing behaviour of MultiChipInput

    const onDelete = (_, index) => {
        const updatedDomains = value.filter((_, i) => i !== index);
        onChange(updatedDomains);
    };

    const onEnter = (_, domain) => {
        const updatedDomains = [...value, domain];
        onChange(updatedDomains);
    };

    const handleValueReset = () => {
        onChange([]);
    };

    return (
        <Flex flexDirection="column" sx={{ gap: 1 }}>
            <InlineField
                label={label}
                fullWidth
                value={value}
                isInputFocused={isInputFocused}
                required
            >
                <Flex alignItems="center" width="100%" height="100%">
                    <MultiChipInput
                        labels={value}
                        onEnter={onEnter}
                        onDelete={onDelete}
                        autoFocus={false}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        borderColor="transparent"
                        showClearAllIcon={false}
                        theme={theme}
                    />
                    <InlineFieldActions>
                        {value.length > 0 ? (
                            <InlineFieldAction
                                icon="cross_circle"
                                onClick={handleValueReset}
                            />
                        ) : null}
                    </InlineFieldActions>
                </Flex>
            </InlineField>
            {isMaraOnlyField && <MaraOnlyTag />}
        </Flex>
    );
};

export default MultiChipTextField;

export const multiChipTextFieldTester = rankWith(
    1,
    uiTypeIs('MultiChipTextField')
);
